import { motion } from "framer-motion";
import { Users, Heart, Baby } from "lucide-react";

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

const highlights = [
  { icon: <Users size={18} />, text: "Two adults welcome" },
  { icon: <Heart size={18} />, text: "Grandparents & family encouraged" },
  { icon: <Baby size={18} />, text: "Bonding at the heart of it" },
];

export default function FamilyInWater() {
  return (
    <section className="pb-24 md:pb-32" data-testid="family-in-water">
      <div className="container mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0A2540]">
          <div className="blob absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#FF7A1A]/25" />
          <div className="blob absolute -bottom-28 -left-24 w-96 h-96 rounded-full bg-[#0EA5E9]/25" />

          <div className="relative grid lg:grid-cols-2 gap-0 items-stretch">
            <div className="p-9 md:p-14 lg:p-16 flex flex-col justify-center">
              <motion.span
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 text-sm text-[#38BDF8] tracking-[0.25em] uppercase font-bold"
              >
                <Heart size={16} /> Our ethos
              </motion.span>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-5 font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.05]"
              >
                Dive in <span className="text-[#FF7A1A]">together</span>.<br className="hidden md:block" /> The whole family, in the water.
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.12 }}
                className="mt-6 text-lg text-[#CBD5E1] leading-relaxed max-w-xl"
              >
                Bonding is at the very heart of Happy Tiddlers. In our{" "}
                <span className="text-white font-semibold">Urchins</span> and{" "}
                <span className="text-white font-semibold">Small Fry</span> classes we actively
                encourage <span className="text-white font-semibold">two adults in the water</span> —
                mum, dad, grandparents, or whoever is special to your little one.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.18 }}
                className="mt-4 font-heading text-xl md:text-2xl font-semibold text-white"
              >
                More loving hands, more giggles, memories you'll treasure forever.
              </motion.p>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {highlights.map((h) => (
                  <span key={h.text} className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm font-semibold text-white" data-testid="family-highlight">
                    <span className="text-[#38BDF8]">{h.icon}</span>
                    {h.text}
                  </span>
                ))}
              </div>

              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-9 self-start bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-1 transition-all duration-300"
                data-testid="family-book-cta"
              >
                Book your family in
              </a>
            </div>

            <div className="relative min-h-[320px] lg:min-h-full">
              <img
                src="https://images.unsplash.com/photo-1761523763930-81932b99b459?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="A parent holding their baby closely in the pool"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-transparent to-transparent lg:from-[#0A2540]/70" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
